// Resolve the font identifiers for a render payload (subtitle_config).
//
// The backend resolves the font file by documentId, but the editor's font
// picker keeps the documentId in a separate piece of state
// (selectedSubtitleFontId) and writes the font's *display name* into
// subtitleData.fontFamily. Persisted subtitle configs store that display
// name too. Sending the display name left the backend unable to resolve a
// fontUrl and clips rendered with the fallback font.
//
// Resolution order: selected font id → fontFamily as id → fontFamily as
// display name/family → raw fontFamily with no url (the backend's own
// name-lookup fallback is the last line of defense).

export interface RenderFontCandidate {
  id: string;
  name?: string | null;
  family?: string | null;
  file_url?: string | null;
}

export function resolveRenderFont({
  selectedFontId,
  fontFamily,
  fonts,
}: {
  selectedFontId: string | null | undefined;
  fontFamily: string;
  fonts: readonly RenderFontCandidate[] | null | undefined;
}): { fontFamily: string; fontUrl: string | undefined } {
  const byId = (id: string | null | undefined) =>
    id ? fonts?.find((font) => font.id === id) : undefined;

  const match =
    byId(selectedFontId) ??
    byId(fontFamily) ??
    fonts?.find((font) => font.name === fontFamily || font.family === fontFamily);

  if (!match) return { fontFamily, fontUrl: undefined };
  return { fontFamily: match.id, fontUrl: match.file_url || undefined };
}
